import { useRef, useState, useEffect, useCallback } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";

type Stage = "idle" | "removing" | "editing" | "saving" | "done";

const CS = 800; // canvas internal resolution (square)

export default function AvatarPage() {
  const [stage, setStage] = useState<Stage>("idle");
  const [photoSrc, setPhotoSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [finalUrl, setFinalUrl] = useState<string | null>(null);
  const [frameReady, setFrameReady] = useState(false);
  const [savedOnline, setSavedOnline] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const photoRef = useRef<HTMLImageElement | null>(null);
  const frameRef = useRef<HTMLImageElement | null>(null);
  const fundoRef = useRef<HTMLImageElement | null>(null);

  // Gesture refs (avoid stale closures in event handlers)
  const zoomRef = useRef(zoom);
  const offsetRef = useRef(offset);
  const touchGesture = useRef<{ lastDist: number | null; lastPos: { x: number; y: number } | null }>({
    lastDist: null,
    lastPos: null,
  });
  const mouseGesture = useRef<{ active: boolean; last: { x: number; y: number } | null }>({
    active: false,
    last: null,
  });

  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { offsetRef.current = offset; }, [offset]);

  // ─── Constraints ─────────────────────────────────────────────────────────────
  // A foto nunca pode ficar menor que o canvas nem se deslocar a ponto de
  // descobrir o círculo. Com zoom=1 (fit-cover) a foto cobre exatamente o
  // canvas, então offsetX ∈ [-(dw-CS)/2, (dw-CS)/2] e idem para Y.

  const clampOffset = useCallback(
    (z: number, o: { x: number; y: number }): { x: number; y: number } => {
      const img = photoRef.current;
      if (!img) return o;
      const fit = Math.max(CS / img.naturalWidth, CS / img.naturalHeight);
      const dw = img.naturalWidth * fit * z;
      const dh = img.naturalHeight * fit * z;
      const maxX = (dw - CS) / 2;
      const maxY = (dh - CS) / 2;
      return {
        x: Math.min(Math.max(o.x, -maxX), maxX),
        y: Math.min(Math.max(o.y, -maxY), maxY),
      };
    },
    [],
  );

  // ─── Draw ───────────────────────────────────────────────────────────────────

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = photoRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const z = zoomRef.current;
    const o = offsetRef.current;

    ctx.clearRect(0, 0, CS, CS);

    // Clip to circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(CS / 2, CS / 2, CS / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    // 1. Fundo (fundo.jpg cover)
    const fundo = fundoRef.current;
    if (fundo && fundo.complete && fundo.naturalWidth > 0) {
      const ff = Math.max(CS / fundo.naturalWidth, CS / fundo.naturalHeight);
      const fw = fundo.naturalWidth * ff;
      const fh = fundo.naturalHeight * ff;
      ctx.drawImage(fundo, (CS - fw) / 2, (CS - fh) / 2, fw, fh);
    } else {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, CS, CS);
    }

    // 2. Foto do usuário (sem fundo) — fit-cover + zoom + pan
    const fit = Math.max(CS / img.naturalWidth, CS / img.naturalHeight);
    const dw = img.naturalWidth * fit * z;
    const dh = img.naturalHeight * fit * z;
    const x = (CS - dw) / 2 + o.x;
    const y = (CS - dh) / 2 + o.y;
    ctx.drawImage(img, x, y, dw, dh);

    ctx.restore();

    // Frame overlay
    const frame = frameRef.current;
    if (frame && frame.complete && frame.naturalWidth > 0) {
      ctx.drawImage(frame, 0, 0, CS, CS);
    } else {
      drawFallbackFrame(ctx);
    }
  }, []);

  function drawFallbackFrame(ctx: CanvasRenderingContext2D) {
    // Circular border
    ctx.save();
    ctx.strokeStyle = "#F8EBC1";
    ctx.lineWidth = 20;
    ctx.beginPath();
    ctx.arc(CS / 2, CS / 2, CS / 2 - 10, 0, Math.PI * 2);
    ctx.stroke();

    // Bottom gradient band (inside circle)
    ctx.beginPath();
    ctx.arc(CS / 2, CS / 2, CS / 2 - 10, 0, Math.PI * 2);
    ctx.clip();
    const grad = ctx.createLinearGradient(0, CS - 160, 0, CS);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.8)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, CS - 160, CS, 160);

    // "DU NADA" text
    ctx.fillStyle = "#F8EBC1";
    ctx.font = `bold ${CS * 0.1}px Oswald, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillText("DU NADA", CS / 2, CS - 60);

    ctx.restore();
  }

  useEffect(() => {
    if (stage === "editing") redraw();
  }, [zoom, offset, stage, frameReady, redraw]);

  // Load assets when entering editing
  useEffect(() => {
    if (stage !== "editing" || !photoSrc) return;

    const img = new Image();
    img.onload = () => { photoRef.current = img; redraw(); };
    img.src = photoSrc;

    const frame = new Image();
    frame.onload = () => { frameRef.current = frame; setFrameReady(true); };
    frame.onerror = () => { frameRef.current = null; setFrameReady(false); };
    frame.src = "/images/frame-avatar.png";

    const fundo = new Image();
    fundo.onload = () => { fundoRef.current = fundo; redraw(); };
    fundo.onerror = () => { fundoRef.current = null; };
    fundo.src = "/images/fundo.jpg";
  }, [stage, photoSrc, redraw]);

  // ─── File input ─────────────────────────────────────────────────────────────

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (photoSrc) URL.revokeObjectURL(photoSrc);
    photoRef.current = null;
    setStage("removing");

    try {
      const { removeBackground } = await import("@imgly/background-removal");
      const blob = await removeBackground(file);
      const url = URL.createObjectURL(blob);
      setPhotoSrc(url);
    } catch {
      // Se falhar, usa a foto original sem remoção de fundo
      setPhotoSrc(URL.createObjectURL(file));
    }

    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setStage("editing");
  };

  // ─── Touch gestures ──────────────────────────────────────────────────────────

  const pinchDist = (t: React.TouchList) => {
    const dx = t[0].clientX - t[1].clientX;
    const dy = t[0].clientY - t[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const canvasScale = () => {
    const c = canvasRef.current;
    if (!c) return { sx: 1, sy: 1 };
    return { sx: CS / c.clientWidth, sy: CS / c.clientHeight };
  };

  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      touchGesture.current = {
        lastPos: { x: e.touches[0].clientX, y: e.touches[0].clientY },
        lastDist: null,
      };
    } else if (e.touches.length === 2) {
      touchGesture.current = { lastPos: null, lastDist: pinchDist(e.touches) };
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    const { sx, sy } = canvasScale();

    if (e.touches.length === 1 && touchGesture.current.lastPos) {
      const dx = (e.touches[0].clientX - touchGesture.current.lastPos.x) * sx;
      const dy = (e.touches[0].clientY - touchGesture.current.lastPos.y) * sy;
      setOffset((o) => clampOffset(zoomRef.current, { x: o.x + dx, y: o.y + dy }));
      touchGesture.current.lastPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2 && touchGesture.current.lastDist) {
      const nd = pinchDist(e.touches);
      const newZ = Math.min(Math.max(zoomRef.current * (nd / touchGesture.current.lastDist!), 1), 5);
      setZoom(newZ);
      setOffset((o) => clampOffset(newZ, o));
      touchGesture.current.lastDist = nd;
    }
  };

  const onTouchEnd = () => {
    touchGesture.current = { lastDist: null, lastPos: null };
  };

  // ─── Mouse gestures ──────────────────────────────────────────────────────────

  const onMouseDown = (e: React.MouseEvent) => {
    mouseGesture.current = { active: true, last: { x: e.clientX, y: e.clientY } };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!mouseGesture.current.active || !mouseGesture.current.last) return;
    const { sx, sy } = canvasScale();
    const dx = (e.clientX - mouseGesture.current.last.x) * sx;
    const dy = (e.clientY - mouseGesture.current.last.y) * sy;
    setOffset((o) => clampOffset(zoomRef.current, { x: o.x + dx, y: o.y + dy }));
    mouseGesture.current.last = { x: e.clientX, y: e.clientY };
  };

  const onMouseUp = () => { mouseGesture.current.active = false; };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const newZ = Math.min(Math.max(zoomRef.current - e.deltaY * 0.002, 1), 5);
    setZoom(newZ);
    setOffset((o) => clampOffset(newZ, o));
  };

  // ─── Save ────────────────────────────────────────────────────────────────────

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setStage("saving");

    // Gera versão comprimida (500×500) para salvar no Firestore
    const small = document.createElement("canvas");
    small.width = 500;
    small.height = 500;
    small.getContext("2d")!.drawImage(canvas, 0, 0, 500, 500);
    const dataUrl = small.toDataURL("image/jpeg", 0.82);

    setFinalUrl(dataUrl);

    addDoc(collection(db, "avatares"), {
      imageBase64: dataUrl,
      criadoEm: serverTimestamp(),
    })
      .then(() => setSavedOnline(true))
      .catch(() => setSavedOnline(false))
      .finally(() => setStage("done"));
  };

  const handleDownload = () => {
    if (!finalUrl) return;
    const a = document.createElement("a");
    a.href = finalUrl;
    a.download = "avatar-dunada.jpg";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const reset = () => {
    if (photoSrc) URL.revokeObjectURL(photoSrc);
    setPhotoSrc(null);
    setFinalUrl(null);
    setSavedOnline(false);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    photoRef.current = null;
    frameRef.current = null;
    fundoRef.current = null;
    setFrameReady(false);
    setStage("idle");
  };

  // ─── Shared background style ─────────────────────────────────────────────────

  const bg: React.CSSProperties = {
    backgroundColor: "#DA422F",
    backgroundImage: "url(/images/bg-evento.png)",
    backgroundSize: "cover",
    backgroundPosition: "center top",
    fontFamily: "var(--font-oswald)",
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={bg}>

      {/* ── IDLE ── */}
      {stage === "idle" && (
        <div className="flex flex-col items-center gap-8 text-center w-full max-w-xs">
          <h1
            className="text-3xl md:text-5xl font-bold uppercase leading-tight"
            style={{ color: "#F8EBC1" }}
          >
            Faça seu avatar<br />personalizado
          </h1>
          <div
            className="w-full aspect-square overflow-hidden rounded-full"
            style={{ border: "3px solid #F8EBC133" }}
          >
            <img
              src="/images/exemplo.jpg"
              alt="Exemplo de avatar"
              className="w-full h-full object-cover"
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
          />
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full py-4 text-base font-bold tracking-[0.2em] uppercase rounded-full transition hover:opacity-80"
            style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
          >
            Enviar foto
          </button>
        </div>
      )}

      {/* ── EDITING ── */}
      {stage === "editing" && (
        <div className="flex flex-col items-center gap-5 w-full max-w-sm">
     <h1
            className="text-3xl md:text-4xl font-bold uppercase leading-tight"
            style={{ color: "#F8EBC1" }}
          >
            Ajuste sua foto
          </h1>
  

          <canvas
            ref={canvasRef}
            width={CS}
            height={CS}
            className="w-full rounded-full touch-none select-none cursor-grab active:cursor-grabbing"
            style={{ border: "2px solid #F8EBC122" }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onWheel={onWheel}
          />

          {/* Zoom slider */}
          <div className="w-full flex items-center gap-4 px-1">
            <span className="text-xl font-bold select-none" style={{ color: "#F8EBC1" }}>−</span>
            <input
              type="range"
              min={100}
              max={500}
              step={1}
              value={Math.round(zoom * 100)}
              onChange={(e) => {
                const newZ = Number(e.target.value) / 100;
                setZoom(newZ);
                setOffset((o) => clampOffset(newZ, o));
              }}
              className="flex-1 cursor-pointer"
              style={{ accentColor: "#F8EBC1" }}
            />
            <span className="text-xl font-bold select-none" style={{ color: "#F8EBC1" }}>+</span>
          </div>

          <div className="flex gap-3 w-full pt-1">
            <button
              onClick={() => setStage("idle")}
              className="flex-1 py-4 text-sm font-bold uppercase tracking-widest rounded-full border-2 transition hover:opacity-70"
              style={{ borderColor: "#F8EBC166", color: "#F8EBC1" }}
            >
              Voltar
            </button>
            <button
              onClick={handleSave}
              className="flex-1 py-4 text-sm font-bold uppercase tracking-widest rounded-full transition hover:opacity-80"
              style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
            >
              Gerar avatar
            </button>
          </div>
        </div>
      )}

      {/* ── REMOVING BACKGROUND ── */}
      {stage === "removing" && (
        <div className="flex flex-col items-center gap-6 text-center px-4">
          <div
            className="animate-spin rounded-full border-8"
            style={{ width: "72px", height: "72px", borderColor: "#F8EBC1", borderTopColor: "transparent" }}
          />
          <p className="text-lg font-bold uppercase tracking-widest" style={{ color: "#F8EBC1" }}>
            Carregando...
          </p>
          <p className="text-xs opacity-60" style={{ color: "#F8EBC1" }}>
            Powered by IA · pode levar alguns segundos
          </p>
        </div>
      )}

      {/* ── SAVING ── */}
      {stage === "saving" && (
        <div className="flex flex-col items-center gap-6">
          <div
            className="animate-spin rounded-full border-8"
            style={{ width: "72px", height: "72px", borderColor: "#F8EBC1", borderTopColor: "transparent" }}
          />
          <p className="text-lg font-bold uppercase tracking-widest" style={{ color: "#F8EBC1" }}>
            Gerando...
          </p>
        </div>
      )}

      {/* ── DONE ── */}
      {stage === "done" && finalUrl && (
        <div className="flex flex-col items-center gap-8 text-center w-full max-w-xs">
          <h1
            className="text-3xl md:text-4xl font-bold uppercase leading-tight"
            style={{ color: "#F8EBC1" }}
          >
            Du nada, o seu avatar está pronto!
          </h1>
          <img
            src={finalUrl}
            alt="Avatar DU NADA"
            className="w-full rounded-full object-cover"
            style={{ aspectRatio: "1/1", border: "3px solid #F8EBC133" }}
          />
          <p className="text-xs -mt-4 opacity-60 leading-relaxed" style={{ color: "#F8EBC1" }}>
            Pressione o dedo sobre o avatar e salve em sua galeria
          </p>
          <div className="flex flex-col gap-3 w-full">
            <button
              onClick={handleDownload}
              className="w-full py-4 text-base font-bold uppercase tracking-widest rounded-full transition hover:opacity-80"
              style={{ backgroundColor: "#F8EBC1", color: "#DA422F" }}
            >
              Baixar avatar
            </button>
            <button
              onClick={reset}
              className="w-full py-4 text-sm font-bold uppercase tracking-widest rounded-full border-2 transition hover:opacity-70"
              style={{ borderColor: "#F8EBC166", color: "#F8EBC1" }}
            >
              Criar outro
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
